import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  useGetVersionsQuery,
  useAllCommentsGenericQuery,
  useLogEntriesByReferenceQuery,
  LogCategory,
  ShiftType,
  LogTemplateType,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useGetPlatformId } from '@/hooks/useGetPlatformId';
import {
  selectedDate as selectedDateAtom,
  selectedWorkpackIdForKpiTracker,
  selectedLogTemplateType,
  selectedWbsFilter,
} from '../state';
import { formatDateToYYYYMMDD } from '../utils';
import { COLLECTION_NAMES } from '@/constants';
import { KPI_NOTE_LOG_METADATA_KEY } from '../constants';

const WORKPACK_COLLECTION_NAME = 'Workpack';
const LOG_ENTRY_COLLECTION_NAME = 'LogEntry';
const PLATFORM_COLLECTION_NAME = 'platform';

export const useKpiHistoryData = () => {
  const structureId = useGetStructureId();
  const workpackId = useAtomValue(selectedWorkpackIdForKpiTracker);
  const platformId = useGetPlatformId();
  const selectedDate = useAtomValue(selectedDateAtom);
  const logTemplateType = useAtomValue(selectedLogTemplateType);
  const wbsFilter = useAtomValue(selectedWbsFilter);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = formatDateToYYYYMMDD(selectedDate);

  const isCostLog = logTemplateType === LogTemplateType.CostLogTemplate;
  const referenceId = isCostLog ? platformId : workpackId;
  const collectionName = isCostLog ? PLATFORM_COLLECTION_NAME : COLLECTION_NAMES.WORKPACK;
  const logCategory = isCostLog ? LogCategory.CostLog : LogCategory.Worklog;

  const { data: worklogLogEntriesData, loading: worklogLogEntriesLoading } =
    useLogEntriesByReferenceQuery({
      variables: {
        input: {
          referenceId: referenceId ?? '',
          ...(!isCostLog && structureId && { structureId }),
          category: logCategory,
          logDate: formattedDate,
          ...(isCostLog && wbsFilter && { wbs: wbsFilter }),
        },
      },
      skip: !referenceId || (!isCostLog && !structureId),
      fetchPolicy: 'cache-and-network',
    });

  /** No `shift` filter: history lists all job-phase / QA-QC log entries for `logDate` (Day + Night). */
  /** QA/QC forms are only available for WorkLogs, not CostLogs */
  const { data: qaqcLogEntriesData, loading: qaqcLogEntriesLoading } =
    useLogEntriesByReferenceQuery({
      variables: {
        input: {
          referenceId: workpackId ?? '',
          ...(structureId && { structureId }),
          category: LogCategory.QaqcForm,
          logDate: formattedDate,
        },
      },
      skip: isCostLog || !structureId || !workpackId,
      fetchPolicy: 'cache-and-network',
    });

  const logEntryIds = useMemo(() => {
    const worklogIds = worklogLogEntriesData?.logEntriesByReference?.map((entry) => entry.id) ?? [];
    const qaqcIds = qaqcLogEntriesData?.logEntriesByReference?.map((entry) => entry.id) ?? [];
    return [...new Set([...worklogIds, ...qaqcIds])];
  }, [worklogLogEntriesData?.logEntriesByReference, qaqcLogEntriesData?.logEntriesByReference]);

  const qaqcLogEntryIdSet = useMemo(
    () => new Set(qaqcLogEntriesData?.logEntriesByReference?.map((entry) => entry.id) ?? []),
    [qaqcLogEntriesData?.logEntriesByReference]
  );

  /** LogEntry versions use `referenceId` as the log entry id; shift lives on the entry, not in version payload. */
  const logEntryShiftById = useMemo(() => {
    const map: Record<string, ShiftType | null | undefined> = {};
    for (const entry of worklogLogEntriesData?.logEntriesByReference ?? []) {
      map[entry.id] = entry.shift;
    }
    for (const entry of qaqcLogEntriesData?.logEntriesByReference ?? []) {
      map[entry.id] = entry.shift;
    }
    return map;
  }, [worklogLogEntriesData?.logEntriesByReference, qaqcLogEntriesData?.logEntriesByReference]);

  const {
    data: workpackVersionsData,
    loading: workpackLoading,
    error: workpackError,
  } = useGetVersionsQuery({
    variables: {
      input: {
        ...(!isCostLog && structureId && { structureId }),
        collectionName: isCostLog ? PLATFORM_COLLECTION_NAME : WORKPACK_COLLECTION_NAME,
        referenceId: referenceId,
        logDate: formattedDate,
        timezone: userTimezone,
      },
    },
    skip: !referenceId || (!isCostLog && !workpackId) || (!isCostLog && !structureId),
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: logEntryVersionsData,
    loading: logEntryVersionsLoading,
    error: logEntryError,
  } = useGetVersionsQuery({
    variables: {
      input: {
        ...(!isCostLog && structureId && { structureId }),
        collectionName: LOG_ENTRY_COLLECTION_NAME,
        referenceIds: logEntryIds,
        logDate: formattedDate,
        timezone: userTimezone,
      },
    },
    skip:
      !referenceId ||
      (!isCostLog && !workpackId) ||
      (!isCostLog && !structureId) ||
      logEntryIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const { data: commentsData, loading: commentsLoading } = useAllCommentsGenericQuery({
    variables: {
      input: {
        collectionName: collectionName,
        referenceId: referenceId as string,
        ...(!isCostLog && structureId && { structureId }),
        metadata: {
          [KPI_NOTE_LOG_METADATA_KEY]: formattedDate,
        },
      },
    },
    skip: !referenceId || (!isCostLog && !workpackId) || (!isCostLog && !structureId),
    fetchPolicy: 'cache-and-network',
  });

  const loading =
    workpackLoading ||
    logEntryVersionsLoading ||
    commentsLoading ||
    worklogLogEntriesLoading ||
    qaqcLogEntriesLoading;
  const error = workpackError || logEntryError;

  return {
    workpackVersionsData,
    logEntryVersionsData,
    commentsData,
    logEntryIds,
    logEntryShiftById,
    qaqcLogEntryIdSet,
    loading,
    error,
    wbsFilter,
  };
};
